'use client';

import { Button, LayoutContainer, Picture } from '@petsmart-ui/sparky';
import cn from 'classnames';
import Link from 'next/link';

import { ContentfulClick } from '@libs/analytics';

import { getImageProps } from '../../../utils/propsImagesItems';
import { Text } from '../../Text';
import type { BrandSpotlightProps } from './types';
import './BrandSpotlight.scss';

export function BrandSpotlight({ leftBlock, leftBlockTextColor, rightBlock }: Readonly<BrandSpotlightProps>) {
    if (rightBlock.length !== 2) {
        return null;
    }

    const [brandCard] = leftBlock;
    const [assetLg, assetSm] = rightBlock;
    const Component = brandCard.cta ? Link : 'div';
    const assets = {
        sm: {
            image: getImageProps(assetSm.image),
            internalTitle: assetSm.internalTitle,
            link: assetSm.link,
        },
        lg: {
            image: getImageProps(assetLg.image),
            internalTitle: assetLg.internalTitle,
            link: assetLg.link,
        },
    };

    return (
        <LayoutContainer className="brand-spotlight">
            {Object.entries(assets).map(([size, { image, link, internalTitle }]) => (
                <ContentfulClick
                    component={Link}
                    key={size}
                    href={link?.webUrl || ''}
                    className={`brand-spotlight__${size}`}
                    eventData={{
                        linkText: link.internalTitle,
                        linkUrl: link?.webUrl ?? '',
                        promoTitle: brandCard.header,
                        promoVariant: 'storytelling card up - Brand Spotlight - image',
                        promoDesc: internalTitle,
                        promoType: `brand-spotlight__${size}`,
                    }}
                >
                    <Picture {...image} />
                </ContentfulClick>
            ))}

            <ContentfulClick
                component={Component}
                className="brand-spotlight__card"
                style={{ background: brandCard.backgroundFill }}
                href={brandCard.cta?.link.webUrl ?? ''}
                eventData={{
                    linkText: cn(brandCard.header, brandCard.bodyText, brandCard.cta?.text),
                    linkUrl: brandCard.cta?.link.webUrl ?? '',
                    promoTitle: brandCard.header,
                    promoVariant: 'storytelling card up - Brand Spotlight - Card',
                    promoDesc: brandCard.internalTitle,
                    promoType: 'brand-spotlight__card',
                }}
                prefetch={false} // https://petsmart.atlassian.net/browse/HCP-594
            >
                <Text color={leftBlockTextColor} as="h2">
                    {brandCard.header}
                </Text>
                <Text color={leftBlockTextColor}>{brandCard.bodyText}</Text>

                {brandCard.cta && (
                    <Button
                        size="sm"
                        text={brandCard.cta.text}
                        variant={brandCard.cta.buttonStyle === 'Primary' ? 'primary' : 'toolbar'}
                    />
                )}
            </ContentfulClick>
        </LayoutContainer>
    );
}
